import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Check,
  Send,
  FileText,
  RefreshCw,
  ChevronRight,
  Save,
  X,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmailComposer from "./EmailComposer";

const CATEGORY_OPTIONS = [
  { value: "custom", label: "Custom" },
  { value: "follow_up", label: "Follow-ups" },
  { value: "thank_you", label: "Thank You" },
  { value: "project", label: "Project" },
  { value: "boundaries", label: "Boundaries" },
  { value: "sales", label: "Sales" },
  { value: "admin", label: "Admin" }
];

export default function CustomTemplates() {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [filledContent, setFilledContent] = useState({ subject: "", body: "" });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [clients, setClients] = useState([]);
  
  // Create/Edit modal state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "custom",
    subject: "",
    body: "",
    description: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchClients();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/user-templates");
      setTemplates(res.data.templates || []);
    } catch (e) {
      console.error("Error fetching templates:", e);
      toast.error("Failed to load custom templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data || []);
    } catch (e) {
      console.error("Error fetching clients:", e);
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setFilledContent({
      subject: template.subject,
      body: template.body
    });
    
    const placeholders = template.placeholders || [];
    const initialVars = {};
    placeholders.forEach(p => { initialVars[p] = ""; });
    setVariables(initialVars);
  };

  const handleVariableChange = (key, value) => {
    const newVars = { ...variables, [key]: value };
    setVariables(newVars);
    
    let newSubject = selectedTemplate.subject;
    let newBody = selectedTemplate.body;
    
    Object.entries(newVars).forEach(([k, v]) => {
      const placeholder = new RegExp(`\\{${k}\\}`, 'g');
      newSubject = newSubject.replace(placeholder, v || `{${k}}`);
      newBody = newBody.replace(placeholder, v || `{${k}}`);
    });
    
    setFilledContent({ subject: newSubject, body: newBody });
  };

  const handleCopy = async () => {
    const fullEmail = `Subject: ${filledContent.subject}\n\n${filledContent.body}`;
    await navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      name: "",
      category: "custom",
      subject: "",
      body: "",
      description: ""
    });
    setShowEditor(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      subject: template.subject,
      body: template.body,
      description: template.description || ""
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim() || !formData.subject.trim() || !formData.body.trim()) {
      toast.error("Please fill in name, subject, and body");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/user-templates/${editingTemplate.id}`, formData);
        toast.success("Template updated!");
      } else {
        await api.post("/user-templates", formData);
        toast.success("Template created!");
      }
      setShowEditor(false);
      fetchTemplates();
      setSelectedTemplate(null);
    } catch (e) {
      console.error("Error saving template:", e);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;
    
    try {
      await api.delete(`/user-templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (e) {
      console.error("Error deleting template:", e);
      toast.error("Failed to delete template");
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await api.post(`/user-templates/${templateId}/duplicate`);
      toast.success("Template duplicated!");
      fetchTemplates();
    } catch (e) {
      console.error("Error duplicating template:", e);
      toast.error("Failed to duplicate template");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-indigo-500 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            My Custom Templates
          </h2>
          <p className="text-indigo-400 text-sm">Create and manage your own email templates</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          data-testid="create-template-btn"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No custom templates yet</h3>
            <p className="text-slate-400 mb-4">
              Create your first template to save time on repetitive emails
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Template List */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400">
              {templates.length} template{templates.length !== 1 ? "s" : ""}
            </h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
              {templates.map((template) => {
                const isSelected = selectedTemplate?.id === template.id;
                const categoryLabel = CATEGORY_OPTIONS.find(c => c.value === template.category)?.label || "Custom";
                
                return (
                  <div
                    key={template.id}
                    className={`w-full text-left p-4 rounded-xl transition-all ${
                      isSelected
                        ? "bg-indigo-500/20 border-2 border-indigo-500/50"
                        : "bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => selectTemplate(template)}
                        className="flex-1 text-left"
                        data-testid={`custom-template-${template.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-indigo-500 truncate">{template.name}</h4>
                          <Badge className="bg-slate-400 text-slate-400 text-xs">
                            {categoryLabel}
                          </Badge>
                        </div>
                        <p className="text-sm text-indigo-400 truncate">{template.subject}</p>
                        {template.description && (
                          <p className="text-xs text-slate-400 truncate mt-1">{template.description}</p>
                        )}
                      </button>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(template)}
                          className="p-1.5 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded"
                          title="Edit"
                          data-testid={`edit-template-${template.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicateTemplate(template.id)}
                          className="p-1.5 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded"
                          title="Duplicate"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-1.5 text-indigo-400 hover:text-red-400 hover:bg-slate-400 rounded"
                          title="Delete"
                          data-testid={`delete-template-${template.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Template Preview */}
          <div>
            {selectedTemplate ? (
              <Card className="bg-slate-800/50 border-slate-700/50 sticky top-4">
                <CardHeader>
                  <CardTitle className="text-indigo-500">{selectedTemplate.name}</CardTitle>
                  {selectedTemplate.description && (
                    <CardDescription className="text-indigo-400">
                      {selectedTemplate.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Variables */}
                  {Object.keys(variables).length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-slate-400">Fill in the blanks</h4>
                      <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                        {Object.keys(variables).map((key) => (
                          <div key={key}>
                            <label className="block text-xs text-slate-400 mb-1">
                              {key.replace(/_/g, " ")}
                            </label>
                            <input
                              type="text"
                              value={variables[key]}
                              onChange={(e) => handleVariableChange(key, e.target.value)}
                              placeholder={key.replace(/_/g, " ")}
                              className="w-full px-2 py-1.5 text-sm bg-slate-900/50 border border-slate-600 rounded text-indigo-500 focus:border-indigo-500 focus:outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-slate-400">Preview</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCopy}
                          className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                          title="Copy to clipboard"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                        </button>
                        <button
                          onClick={() => setShowEmailComposer(true)}
                          className="flex items-center gap-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg transition-colors text-sm"
                          data-testid="send-custom-template-btn"
                        >
                          <Send className="w-4 h-4" />
                          Send
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-sm text-indigo-400 mb-2">
                        <span className="text-slate-400">Subject:</span>{" "}
                        <span className="text-indigo-500">{filledContent.subject}</span>
                      </p>
                      <div className="border-t border-slate-700 pt-3">
                        <pre className="whitespace-pre-wrap text-sm text-slate-600 font-sans">
                          {filledContent.body}
                        </pre>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800/50 border-slate-700/50">
                <CardContent className="py-12 text-center">
                  <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-400 mb-2">Select a template</h3>
                  <p className="text-slate-400">
                    Choose a custom template from the list to preview and use
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">
              {editingTemplate ? "Edit Template" : "Create New Template"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Template Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Weekly Status Update"
                  className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-400">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {CATEGORY_OPTIONS.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-indigo-500">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-400">Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of when to use this template"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-400">Subject Line</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Weekly Update: {project_name}"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                data-testid="template-subject-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Use {"{placeholder}"} for dynamic content
              </p>
            </div>
            
            <div>
              <Label className="text-slate-400">Email Body</Label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Hi {recipient_name},

Here's your weekly update on {project_name}...

Best regards,
{your_name}"
                rows={10}
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1 font-mono text-sm"
                data-testid="template-body-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowEditor(false)}
              className="px-4 py-2 text-indigo-400 hover:text-indigo-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              data-testid="save-template-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {editingTemplate ? "Update" : "Create"} Template
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={`Subject: ${filledContent.subject}\n\n${filledContent.body}`}
        clients={clients}
      />
    </div>
  );
}
